<script setup lang="ts">
import { GameState, useGameState } from "@/stores/gameState";
import { storeToRefs } from "pinia";
import TopBar from "@/components/topbar/TopBar.vue";
import Strategy from "@/components/Strategy.vue";
import SuggestedActions from "@/components/SuggestedActions.vue";
import CardsInHandDesktop from "@/components/gameActions/CardsInHandDesktop.vue";
import ArrowLeftIcon from "@/assets/double-arrow-left.svg";
import ArrowRightIcon from "@/assets/double-arrow-right.svg";
import ArrowUpIcon from "@/assets/arrow-up.svg";
import StatsIcon from "@/assets/stats.svg";
import ActionsIcon from "@/assets/actions.svg";
import CardsHistoryIcon from "@/assets/cards-history.svg";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import {
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
} from "radix-vue";
import { SuggestedActionQuality, type StrategyData } from "@acepoker/api-core";
import { getStrategy } from "@/api/getStrategy";
import { getHistory } from "@/api/getHistory";
import { getStatistics } from "@/api/getStatistics";
import { getMovesQuality } from "@/api/getMovesQuality";
import { MovesQuality } from "@/types";
import ActionLabel from "@/components/actions/ActionLabel.vue";
import { actionIcons } from "@/constants";
import ScoreWidget from "@/components/score/ScoreWidgetMobile.vue";
import Divider from "@/components/ui/Divider.vue";
import HistoryVirtualScroller from "@/components/history/HistoryVirtualScroller.vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import DotsLoader from "@/components/DotsLoader.vue";
import NoDataMessage from "@/components/NoDataMessage.vue";
import Score from "@/views/Score.vue";
import ExitIcon from "@/assets/exit.svg";
import SettingsSvg from "@/assets/settings.svg";
import CloseSvg from "@/assets/X.svg";
import EndRoundModal from "@/components/modals/EndRound.vue";
import UiHeader from "@/components/Header.vue";
import { useI18n } from "vue-i18n";
import OptionsMenu from "@/views/OptionsMenu.vue";
import UiButton from "@/components/ui/button/Button.vue";
import type {
  PlayerHandHistory,
  PlayerMoveQuality,
  PlayerStatistic,
  SuggestedAction,
} from "@acepoker/contract";
import {
  PLAYER_ROLES,
  PLAYER_ROLES_WITH_STRADDLES,
} from "@acepoker/contract/acepoker";
import WaitForYourTurn from "@/components/ui/WaitForYouTurn.vue";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { usePlayerSettings } from "@/stores/playerSettings";

import PokerTable from "./components/table/PokerTable.vue";
import NLHSeat from "./components/table/NLHSeat.vue";

const { t } = useI18n();
const router = useRouter();

const {
  roomState,
  player,
  roomId,
  gameState,
  isMyTurn,
  lastAction,
  showButtons,
  isReviewMode,
  isHintMode,
  isHintMove,
  suggestedActions,
  loadState,
} = storeToRefs(useGameState());
const { sitDown } = useGameState();
const { nickname, avatarId } = storeToRefs(usePlayerSettings());

watch(loadState, (newValue) => {
  if (newValue === "loaded") {
    sitDown({ name: nickname.value, avatarId: avatarId.value });
  }
});

const playerId = computed(() => player.value?.playerId ?? null);

const isEndRoundVisible = ref(false);

const queryClient = useQueryClient();

const {
  fetchStatus: strategyDataFetchStatus,
  data: strategyData,
  error: strategyDataError,
  refetch: updateStrategy,
} = useQuery({
  queryKey: [roomId, isMyTurn],
  queryFn: async ({ queryKey }) => {
    if (!queryKey[1]) {
      throw new Error("error.WaitYourTurnToReceiveData");
    }
    if (!queryKey[0]) {
      throw new Error("error.NoRoomId");
    }
    const response = await getStrategy(queryKey[0] as string).catch((e) => {
      console.log(e);
      return new Error("error.FailedToReceiveDataFromAI");
    });
    if (response instanceof Error) {
      throw response;
    }
    return response;
  },
  retry: 1,
});

const {
  isPending: isHistoryDataLoading,
  data: historyData,
  error: historyDataError,
  refetch: updateHistory,
} = useQuery({
  queryKey: [playerId, "nlh-history"],
  queryFn: async ({ queryKey }) => {
    if (!queryKey[0]) {
      throw new Error("error.NoPlayerId");
    }
    const response = await getHistory(queryKey[0]).catch((e) => {
      console.log(e);
      return new Error("error.FailedToFetchData");
    });
    if (response instanceof Error) {
      throw response;
    }
    return response.items;
  },
  retry: 1,
});

const statisticScope = ref("session");

const {
  isPending: isStatisticsDataLoading,
  data: statisticsData,
  error: statisticsDataError,
  refetch: updateStatistics,
} = useQuery({
  queryKey: [playerId, statisticScope, isEndRoundVisible, "nlh-statistics"],
  queryFn: async ({ queryKey }) => {
    if (!queryKey[0]) {
      throw new Error("error.NoPlayerId");
    }
    const sessionId =
      queryKey[1] === "session" || isEndRoundVisible.value
        ? roomState.value.sessionId
        : undefined;
    const response = await Promise.all([
      getStatistics(queryKey[0] as string, sessionId),
      getMovesQuality(queryKey[0] as string, sessionId),
    ]).catch((e) => {
      console.log(e);
      return new Error("error.FailedToFetchData");
    });

    if (response instanceof Error) {
      throw response;
    }
    const [statistics, moves] = response;
    return { moves, statistics };
  },
  retry: 1,
  staleTime: 0,
});

const leftPanelOpen = ref(false);
const rightPanelOpen = ref(false);
const leftPanelOptions = ref<string[]>([]);
const rightPanelOptions = ref<string[]>([]);

const toggleLeftPanelOption = (option: string, onOpen?: () => void) => {
  if (leftPanelOptions.value.includes(option)) {
    leftPanelOptions.value = leftPanelOptions.value.filter(
      (entry) => entry !== option,
    );
  } else {
    leftPanelOptions.value = [...leftPanelOptions.value, option];
    onOpen?.();
  }
};

const toggleRightPanelOption = (option: string, onOpen?: () => void) => {
  if (rightPanelOptions.value.includes(option)) {
    rightPanelOptions.value = rightPanelOptions.value.filter(
      (entry) => entry !== option,
    );
  } else {
    rightPanelOptions.value = [...rightPanelOptions.value, option];
    onOpen?.();
  }
};

const openRightPanelWithOption = (option: string, onEnd: () => void) => {
  rightPanelOpen.value = true;
  setTimeout(() => toggleRightPanelOption(option), 300);
  onEnd();
};

const openLeftPanelWithOption = (option: string, onEnd: () => void) => {
  leftPanelOpen.value = true;
  setTimeout(() => toggleLeftPanelOption(option), 300);
  onEnd();
};

const openRightPanelFull = () => {
  rightPanelOpen.value = true;

  updateHistory();
  updateStatistics();

  setTimeout(() => {
    rightPanelOptions.value = ["history", "analysis"];
  }, 300);
};

const onEndRound = () => {
  isEndRoundVisible.value = true;
};

const pushToHistory = (
  action: SuggestedAction,
  suggestedActions: SuggestedAction[],
) => {
  queryClient.setQueryData(
    [playerId, "nlh-history"],
    (historyData: PlayerHandHistory[]) => {
      const moves = historyData[0]?.moves || [];
      const roles = roomState.value.gameConfig?.blinds[2]
        ? PLAYER_ROLES_WITH_STRADDLES
        : PLAYER_ROLES;
      const roleIndex = roomState.value.seats
        .filter((entry) => entry !== null)
        .findIndex(
          (entry) => entry?.playerId === roomState.value.hand?.activePlayerId,
        );
      const role =
        roles[roomState.value.players.length as keyof typeof roles][roleIndex];
      const newMoves = [
        {
          ...action,
          suggestedActions,
          id: Date.now(),
          sessionId: historyData[0]?.sessionId || roomState.value.sessionId,
          handHistory: [
            ...roomState.value.handHistory,
            {
              type: "action",
              action: action.actionName as
                | "fold"
                | "check"
                | "call"
                | "bet"
                | "raise",
              amount: action.amount as number,
              totalBet: 0,
              role,
            },
          ],
          communityCards: roomState.value.hand?.communityCards.join("") ?? "",
          moveId: "removeme",
          playerCards:
            roomState.value.hand?.holeCards[
              roomState.value.hand.activePlayerId || ""
            ]?.join("") ?? "",
          createdAt: "",
        },
        ...moves,
      ];

      const moveCorrect =
        action.quality === SuggestedActionQuality.Best ||
        action.quality === SuggestedActionQuality.Correct;

      if (
        !historyData[0] ||
        historyData[0].sessionId !== roomState.value.sessionId
      ) {
        return [
          {
            id: Date.now(),
            sessionId: roomState.value.sessionId,
            correctMoves: Number(moveCorrect),
            totalMoves: 1,
            moves: newMoves,
            playerId,
            lastMoveAt: new Date().toISOString(),
            createdAt: new Date().toISOString(),
          },
          ...historyData.slice(0),
        ];
      }

      return [
        {
          ...historyData[0],
          moves: newMoves,
          correctMoves: historyData[0].correctMoves + Number(moveCorrect),
          totalMoves: historyData[0].totalMoves + 1,
        },
        ...historyData.slice(1),
      ];
    },
  );
};

const pushToStatistics = (action: SuggestedAction) => {
  queryClient.setQueryData(
    [playerId, statisticScope, isEndRoundVisible, "nlh-statistics"],
    (data: {
      moves: {
        items: PlayerMoveQuality[];
        total: number;
      };
      statistics: PlayerStatistic;
    }) => {
      const totalEVLossDelta = action.ev > 0 ? 0 : action.ev;
      const handsTotal = data.statistics.handsTotal + 1;
      const totalEVLoss = data.statistics.totalEVLoss + totalEVLossDelta;
      const avgEVLoss = totalEVLoss / handsTotal;

      return {
        moves: {
          ...data.moves,
          items: data.moves.items.find(
            (item) => item.quality === action.quality,
          )
            ? data.moves.items.map((item) =>
                action.quality === item.quality
                  ? { ...item, count: item.count + 1 }
                  : item,
              )
            : [
                ...data.moves.items,
                {
                  id: Date.now(),
                  quality: action.quality,
                  sessionId: null,
                  playerId,
                  count: 1,
                },
              ],
        },
        statistics: {
          ...data.statistics,
          handsTotal,
          totalEVLoss,
          avgEVLoss,
        },
      };
    },
  );
};

const handleExit = (args?: { close?: boolean }) => {
  onEndRound();
};
</script>

<template>
    <div class="flex flex-col items-center w-full h-full">
      <ui-header class="py-5 px-10">
        <template v-slot:buttons>
          <div class="flex items-center">
            <score class="mr-2" reverse-delta-position />
            <div class="mr-2 text-white truncate">{{ roomState.name }}</div>

            <options-menu
              v-slot="{ isMenuVisible }"
              @exit="handleExit()"
              @exit-and-close="handleExit({ close: true })"
            >
              <div class="relative w-10 h-10">
                <transition name="options-menu">
                  <div
                    v-if="isMenuVisible"
                    class="absolute w-10 h-10 mr-2 cursor-pointer p-2 rounded-[8px] bg-8 text-white flex items-center justify-center gap-2"
                  >
                    <close-svg />
                  </div>
                  <div
                    v-else
                    class="absolute w-10 h-10 mr-2 cursor-pointer p-2 rounded-[8px] bg-menu-item-bg text-white flex items-center justify-center gap-2"
                  >
                    <settings-svg />
                  </div>
                </transition>
              </div>
            </options-menu>
            <div
              class="cursor-pointer p-2 rounded-[8px] bg-menu-item-bg text-white flex items-center gap-2"
              @click="onEndRound"
            >
              <exit-icon />
            </div>
          </div>
        </template>
      </ui-header>
      <top-bar
        :handHistory="roomState.handHistory"
        class="h-[76px] pl-[10px] mt-[-18px] pt-[15px] pb-[40px] pr-[5px] md:pt-[10px]"
      />

      <poker-table class="room__table">
        <template #table-seat="seatProps">
          <NLHSeat v-bind="seatProps" />
        </template>
      </poker-table>
      <transition appear name="room__animation-bottom-slide">
        <div
          class="flex justify-center flex-auto items-end mb-[50px]"
          v-if="
            roomState.gameConfig?.learningMode &&
            gameState === GameState.GAME &&
            showButtons
          "
        >
          <div class="relative">
            <suggested-actions
              size="large"
              :actions="suggestedActions"
              :lastActionNameRaw="lastAction?.actionNameRaw"
              :isReviewMode="isReviewMode"
              :isHintMode="isHintMode || isHintMove || isReviewMode"
              @button-click="
                async (action) => {
                  if (!isMyTurn) return;
                  await action.fn();
                  lastAction = action;
                  pushToHistory(action, suggestedActions);
                  pushToStatistics(action);
                }
              "
            />
          </div>
        </div>
        <cards-in-hand-desktop
          v-else-if="
            !roomState.gameConfig?.learningMode && gameState === GameState.GAME
          "
          class="room__game-actions"
        />
      </transition>
      <div
        :class="{
          'z-9999 absolute top-[120px] bottom-[10px] bg-dark-grey rounded px-[6px] py-[10px] transition-all left-2': true,
          'w-[44px]': !leftPanelOpen,
          'w-[320px]': leftPanelOpen,
        }"
      >
        <div class="flex flex-col h-full justify-center">
          <div class="grow flex flex-col overflow-y-auto overflow-x-hidden">
            <template v-if="leftPanelOpen">
              <collapsible-root :open="leftPanelOptions.includes('actions')">
                <div
                  class="flex items-center gap-2 font-semibold text-base p-2 cursor-pointer"
                  @click="toggleLeftPanelOption('actions', updateStrategy)"
                >
                  <actions-icon />
                  <div class="grow text-white">Actions</div>
                  <arrow-up-icon
                    :class="{
                      'transition-all': true,
                      'rotate-180': !leftPanelOptions.includes('actions'),
                    }"
                  />
                </div>
                <collapsible-content class="">
                  <div class="flex justify-center p-2">
                    <dots-loader
                      v-if="strategyDataFetchStatus === 'fetching'"
                      size="medium"
                    />
                    <div v-else-if="strategyData" class="w-full">
                      <strategy :strategy-data="strategyData" />
                    </div>
                    <div
                      v-else-if="
                        strategyDataError &&
                        strategyDataError.message !==
                          'error.WaitYourTurnToReceiveData'
                      "
                      class="text-secondary text-sm"
                    >
                      {{ t(strategyDataError.message) }}
                    </div>
                    <wait-for-your-turn
                      v-else-if="
                        strategyDataError &&
                        strategyDataError.message ===
                          'error.WaitYourTurnToReceiveData'
                      "
                    />
                  </div>
                </collapsible-content>
              </collapsible-root>
            </template>
            <template v-else>
              <div
                class="rounded-lg bg-1 p-1 cursor-pointer"
                @click="openLeftPanelWithOption('actions', updateStrategy)"
              >
                <actions-icon />
              </div>
            </template>
          </div>
          <div class="flex justify-end">
            <arrow-right-icon
              :class="{
                'transition-all cursor-pointer': true,
                'rotate-180': leftPanelOpen,
              }"
              @click="
                () => {
                  if (leftPanelOpen) {
                    leftPanelOpen = false;
                    leftPanelOptions = [];
                  } else {
                    openLeftPanelWithOption('actions', updateStrategy);
                  }
                }
              "
            />
          </div>
        </div>
      </div>
      <div
        :class="{
          'z-9999 absolute top-[120px] bottom-[10px] bg-dark-grey rounded px-[6px] py-[10px] transition-all right-2': true,
          'w-[44px]': !rightPanelOpen,
          'w-[320px]': rightPanelOpen,
        }"
      >
        <div class="flex flex-col h-full justify-center">
          <div
            class="grow flex flex-col gap-[10px] overflow-y-auto overflow-x-hidden shadow-bottom"
          >
            <template v-if="rightPanelOpen">
              <collapsible-root :open="rightPanelOptions.includes('analysis')">
                <div
                  class="flex items-center gap-2 font-semibold text-base p-2 cursor-pointer"
                  @click="toggleRightPanelOption('analysis', updateStatistics)"
                >
                  <stats-icon />
                  <div class="grow text-white">Analysing moves</div>
                  <arrow-up-icon
                    :class="{
                      'transition-all': true,
                      'rotate-180': !rightPanelOptions.includes('analysis'),
                    }"
                  />
                </div>
                <collapsible-content
                  class="data-[state=open]:animate-slide-down data-[state=closed]:animate-slide-up overflow-hidden"
                >
                  <div class="flex flex-col p-2 items-center">
                    <toggle-group
                      v-model="statisticScope"
                      type="single"
                      class="w-full flex justify-end mb-4"
                    >
                      <toggle-group-item value="session" variant="rounded">
                        {{ $t("Statistic.button.Session") }}
                      </toggle-group-item>
                      <ToggleGroupItem
                        value="total"
                        variant="rounded"
                        ref="totalBtn"
                      >
                        {{ $t("Statistic.button.Total") }}
                      </ToggleGroupItem>
                    </toggle-group>
                    <dots-loader v-if="isStatisticsDataLoading" size="medium" />
                    <template v-else-if="statisticsData">
                      <div class="w-full flex flex-col p-1">
                        <action-label
                          v-for="move in statisticsData.moves.items"
                          :key="move.quality"
                          :label="MovesQuality[move.quality]"
                          :icon="actionIcons[move.quality]"
                          :count="Number(move.count)"
                          size="small"
                        />
                      </div>
                      <score-widget
                        :statistic="statisticsData.statistics"
                        size="small"
                        class="text-white"
                      />
                    </template>
                    <div
                      v-else-if="statisticsDataError"
                      class="text-secondary text-sm"
                    >
                      {{ t(statisticsDataError.message) }}
                    </div>
                  </div>
                </collapsible-content>
              </collapsible-root>
              <divider />
              <collapsible-root
                :open="rightPanelOptions.includes('history')"
                class="overflow-hidden"
              >
                <div
                  class="flex items-center gap-2 font-semibold text-base p-2 cursor-pointer"
                  @click="toggleRightPanelOption('history', updateHistory)"
                >
                  <cards-history-icon />
                  <div class="grow text-white">History</div>
                  <arrow-up-icon
                    :class="{
                      'transition-all': true,
                      'rotate-180': !rightPanelOptions.includes('history'),
                    }"
                  />
                </div>
                <collapsible-content
                  class="data-[state=open]:animate-slide-down data-[state=closed]:animate-slide-up overflow-hidden h-full"
                >
                  <div class="flex flex-col p-2 items-center text-white h-full">
                    <dots-loader v-if="isHistoryDataLoading" size="medium" />
                    <NoDataMessage
                      v-else-if="historyData && !historyData.length"
                    >
                      <template #img>
                        <img
                          src="@/assets/empty-hand-history.png"
                          class="w-2/3"
                        />
                      </template>
                      {{ $t("CustomDrills.placeholder.EmptyHistoryTitle") }}
                      <template #message>{{
                        $t("CustomDrills.placeholder.EmptyHistoryText")
                      }}</template>
                    </NoDataMessage>
                    <history-virtual-scroller
                      v-else-if="historyData && historyData.length"
                      :data="historyData"
                      class="w-full h-full"
                    />
                    <div
                      v-else-if="historyDataError"
                      class="text-secondary text-sm"
                    >
                      {{ t(historyDataError.message) }}
                    </div>
                  </div>
                </collapsible-content>
              </collapsible-root>
            </template>
            <template v-else>
              <div
                class="rounded-lg bg-1 p-1 cursor-pointer"
                @click="openRightPanelWithOption('analysis', updateStatistics)"
              >
                <stats-icon />
              </div>
              <div
                class="rounded-lg bg-1 p-1 cursor-pointer"
                @click="openRightPanelWithOption('history', updateHistory)"
              >
                <cards-history-icon />
              </div>
            </template>
          </div>
          <div class="flex justify-start">
            <arrow-left-icon
              :class="{
                'transition-all cursor-pointer': true,
                'rotate-180': rightPanelOpen,
              }"
              @click="
                () => {
                  if (rightPanelOpen) {
                    rightPanelOpen = false;
                    rightPanelOptions = [];
                  } else {
                    openRightPanelFull();
                  }
                }
              "
            />
          </div>
        </div>
      </div>
    </div>
    <transition name="end-round-modal-fade">
      <end-round-modal
        v-if="statisticsData && isEndRoundVisible"
        :open="isEndRoundVisible"
        :moves="statisticsData.moves.items"
        :statistic-info="statisticsData.statistics"
        @cancel="isEndRoundVisible = false"
      >
        <template #button-primary>
          <ui-button
            @click="router.push('/games')"
            class="w-1/2 text-white"
            variant="default2"
            >{{ $t("EndSession.button.NextPractice") }}</ui-button
          >
        </template>
        <template #button-secondary>
          <a @click="router.push('/games')" class="w-1/2">
            <ui-button class="w-full text-white" variant="ghostOutlined">{{
              $t("EndSession.button.Exit")
            }}</ui-button>
          </a>
        </template>
      </end-round-modal>
    </transition>
</template>

<style>
.end-round-modal-fade-enter-active,
.end-round-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.end-round-modal-fade-enter-from,
.end-round-modal-fade-leave-to {
  opacity: 0;
}

.shadow-bottom::after {
  content: "";
  position: absolute;
  bottom: 40px;
  left: 0;
  height: 60px;
  width: 100%;
  pointer-events: none;
  background: linear-gradient(to top, rgba(31, 31, 36, 1), rgba(31, 31, 36, 0));
}
</style>
